import { computed } from 'mobx';
import { IEntityStore, IHarmonization, ISupplierItem, ISupplierItemExt } from '../../../main/entities';
import { MgHasher } from '../../../mg/MgHasher';
import { IOcResponse } from '../../../services/Communicator';
import { Momentino } from '../../../services/Momentino';
import { AbstractRepo } from '../../../stores/internal/AbstractRepo';
import { IMultirepo } from '../../../stores/Multirepo';

export class SupplierItemExtended implements ISupplierItem, ISupplierItemExt {
	constructor(
		private readonly supplierItem: ISupplierItem,
		private readonly supplierItemExt: ISupplierItemExt|undefined,
		public readonly supplierName: string | undefined,
		public readonly brand: string | undefined,
		public readonly nHarmonizations: number,
		public readonly hasAnyHarmonization: boolean,
		private readonly maxHarmonizations: number,
		public readonly harmonization: IHarmonization | undefined,
		public readonly timestamp: string,
	) {}

	get id() {return this.supplierItem.id;}
	get isDeleted() {return this.supplierItem.isDeleted;}
	get name() {return this.supplierItem.name;}

	get itemNo() {return this.supplierItem.itemNo;}
	get supplierId() {return this.supplierItem.supplierId;}
	get itemGroup1() {return this.supplierItem.itemGroup1;}
	get itemGroup2() {return this.supplierItem.itemGroup2;}
	get packaging() {return this.supplierItem.packaging;}
	get imageLink() {return this.supplierItem.imageLink;}
	get eancode() {return this.supplierItem.eancode;}
	get supplierItemExtId() {return this.supplierItem.supplierItemExtId;}
	get price() {return this.supplierItem.price;}
	get brandId() {return this.supplierItem.brandId;}

	get nHarmonizedCatalogs() {return this.supplierItemExt?.nHarmonizedCatalogs ?? -1;} //TODO
	get nDeharmonizedCatalogs() {return this.supplierItemExt?.nDeharmonizedCatalogs ?? -1;} //TODO
	get product() {return this.supplierItemExt?.product ?? '?';}
	get variation() {return this.supplierItemExt?.variation ?? '?';}
	get description() {return this.supplierItemExt?.description ?? '?';}
	get age() {return this.supplierItemExt?.age ?? '?';}
	get vol() {return this.supplierItemExt?.vol ?? '?';}
	get container() {return this.supplierItemExt?.container ?? '?';}
	get origin() {return this.supplierItemExt?.origin ?? '?';}
	get bundle() {return this.supplierItemExt?.bundle ?? '?';}
	get amount() {return this.supplierItemExt?.amount ?? 0;}
	get unit() {return this.supplierItemExt?.unit ?? '?';}

	get state() {
		return this.harmonization?.state ?? 'DELETED';
	}
	get harmoStatus() : string {
		return this.nHarmonizations + '/' + this.maxHarmonizations;
	}
}

export class SupplierItemExtendedRepo extends AbstractRepo<SupplierItemExtended> implements IEntityStore<SupplierItemExtended> {
	private usedRepos = new Set<IEntityStore>();

	private constructor(
		id: string,
		private readonly multirepo: IMultirepo,
		private readonly maxHarmonizations: number,
		supplierItemRepos: Array<IEntityStore<ISupplierItem>>,
	) {
		super(id, () => supplierItemRepos.flatMap(r => r.allAsList.map(i => this.newItem(i))));
		this.usedRepos.add(multirepo.masterSupplierRepo);
		this.usedRepos.add(multirepo.registeredSupplierRepo);
		this.usedRepos.add(multirepo.brandRepo);
		this.usedRepos.add(multirepo.harmonizationRepo);
		this.usedRepos.add(multirepo.harmonizerRepo);
		supplierItemRepos.forEach(r => {
			this.usedRepos.add(r);
			const rExt = multirepo.supplierItemExtRepo(+r.id.replace(/.*:/, ''));
			this.usedRepos.add(rExt);
		});
	}

	static newRepo(
		id: string,
		multirepo: IMultirepo,
		maxHarmonizations: number,
		supplierItemRepos: Array<IEntityStore<ISupplierItem>>,
	) : IEntityStore<SupplierItemExtended> {
		return new SupplierItemExtendedRepo(id, multirepo, maxHarmonizations, supplierItemRepos);
	}

	private newItem(supplierItem: ISupplierItem) : SupplierItemExtended {
		const m = this.multirepo;
		const supplierItemExt = m.supplierItemExtRepo(supplierItem.supplierId).byId(supplierItem.supplierItemExtId);
		const supplier = m.registeredSupplierRepo.byId(supplierItem.supplierId);
		const supplierName = supplier?.name;
		const brand = m.brandRepo.byId(supplierItem.brandId)?.name;
		const nHarmonizations = m.harmonizationRepo.primaryCountsByItemId.count(supplierItem.id);
		const hasAnyHarmonization = m.harmonizationRepo.hasAnyHarmonizationByItemId(supplierItem.id);
		const maxHarmonizations = this.maxHarmonizations;
		const harmonization = m.harmonizationRepo.byId(supplierItem.id)
			?? m.harmonizationRepo.lastHarmonizationByMasterItemId(supplierItem.id);
		const timestamp = Momentino.millisTo.timestampWithMillis(Date.now()); //TODO get some real timestamp
		return new SupplierItemExtended(supplierItem, supplierItemExt, supplierName, brand,
			nHarmonizations, hasAnyHarmonization, maxHarmonizations,
			harmonization, timestamp);
	}

	@computed get etag() {
		const repos = [...this.usedRepos];
		//console.log(repos.filter(r => !r.etag).map(r => r.id));
		for (const r of repos) {
			if (!r.etag) return '';
		}
		return new MgHasher().addUnordered(repos.map(r => r.etag + ' ' + r.id)).addString(this.id).toString();
	}

	fetch(mode?: 'NORMAL' | 'DIRECT' | 'INVALIDATE') : Promise<unknown> {
		return Promise.all([...this.usedRepos].map(r => r.fetch(mode)));
	}

	handleResponse(responsePromise: Promise<IOcResponse>, successMessage: string) : Promise<unknown> {
		throw new Error('Unsupported handleResponse in SupplierItemExtendedRepo ' + this.id);
	}
}
